import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { SpacesService, SpaceType } from 'account-hybrid/common/components/spaces';
import { FeatureFlagsService, IdentityService, LicenseType, SessionService } from 'account-hybrid/core/authentication';
import { LicenseService } from 'account-hybrid/core/license.service';

/**
 * Native-Angular port of the AngularJS `ftAdvocacy` shell component
 * (account-v2/.../advocacy/group/advocacy.component.ts + advocacy.html).
 * Shell-first pattern — same as PeopleComponent (M3). Top-level Angular
 * state so the Angular sub-tree can host its AngularJS leaf states
 * (bigPicture, contests, audiences, journeys, contest-container modals,
 * ftPeopleDetails) via <ft-ui-view-wrapper>.
 *
 * Namespace `advocacy-group` (not `advocacy`) because `app/features/advocacy`
 * is already occupied by the newer (incomplete) `advocacy-new` design at
 * `/{space}/advocacy-new`. This is the legacy `/{space}/advocacy` migration.
 *
 * Markup mirrors account-v2/.../advocacy/group/advocacy.html so the
 * `/advocacy*` routes stay pixel/css-parity against the frozen baseline.
 */
@Component({
    selector: 'ft-advocacy-group',
    templateUrl: './advocacy-group.component.html',
    standalone: false
})
export class AdvocacyGroupComponent implements OnInit {
  hasLicense = false;
  canSeeCampaigns = false;
  totalPropertiesWithLicense: number | null = null;
  property: any;
  company: any;

  constructor(
    public readonly identity: IdentityService,
    private readonly spacesService: SpacesService,
    private readonly licenseService: LicenseService,
    private readonly sessionService: SessionService,
    private readonly featureFlagsService: FeatureFlagsService,
    private readonly store: Store<any>,
  ) {}

  ngOnInit(): void {
    this.hasLicense = this.licenseService.checkCurrentSpaceLicense(LicenseType.Advocacy);
    // The `campaigns` (advocacy.contests) tab is visible to flip.to staff OR to any
    // account that has not opted out via the IsCampaignsDisabled company feature flag
    // — which defaults to false, so hoteliers see the tab unless it was switched off
    // in Account Settings. Restores the legacy AngularJS ft-branches condition
    // `vm.identity.isFliptoStaff() || !vm.FeatureFlags.isCampaignsDisabled()`; the
    // shell-first port kept only the staff half, hiding the tab from every hotelier
    // (FTK-7517).
    this.canSeeCampaigns = this.identity.isFliptoStaff() || !this.featureFlagsService.isCampaignsDisabled();
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);
    // On a Company-space landing (no current-space license) the teaser shows a
    // company-level count: "{N} of your properties are reaching...", where N is
    // the company properties holding the Advocacy license. Verbatim port of the
    // legacy AngularJS AdvocacyController (account-v2/.../advocacy/group/
    // advocacy.component.ts) — computed from the session (deterministic), not /api.
    // A Property/Mixed space leaves totalPropertiesWithLicense null → the
    // property-level empty-state copy renders (legacy parity).
    if (this.spacesService.current?.type === SpaceType.Company) {
      this.company = this.sessionService.getCompany(this.spacesService.getCompanyUuid()!);
      const properties = this.sessionService.getCompanyProperties(this.spacesService.getCompanyUuid()!);
      this.totalPropertiesWithLicense = properties.filter(
        (p: any) => this.licenseService.checkLicense(p.Licenses, LicenseType.Advocacy),
      ).length;
    }
  }

  // Opens the spaces selector — verbatim port of the legacy controller's
  // showSpaces() (dispatched the same "[Spaces] Set isVisible" NgRx action via
  // ngrxStore; the native Store is that same store). Drives the "Check out each
  // property to see the impact" teaser link.
  showSpaces(): void {
    this.store.dispatch({ type: '[Spaces] Set isVisible', payload: true } as any);
  }
}
